/**
 * CheckoutDrawer Component (Luxury Design)
 * Embedded checkout functionality for mobile app
 *
 * Features:
 * - Full-screen modal with luxury aesthetic
 * - Tabs: Items, Products, Discounts, Payments
 * - Swipe-to-delete service cards
 * - Square borders, monochromatic design
 * - Serif fonts for prices and totals
 * - Dark mode support
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  X, 
  Minimize2, 
  ShoppingCart, 
  Package, 
  Percent, 
  CreditCard,
  Plus,
  Minus,
  DollarSign,
  User,
  Clock,
  Trash2,
  CheckCircle
} from 'lucide-react';

import { useMobileAppCheckout } from '../hooks/useCheckout';
import { removeServiceFromAppointmentGroup } from '../services/appointmentService';

const CheckoutDrawer = ({
  isOpen,
  isMinimized,
  groups,
  onClose,
  onMinimize,
  onGroupAdd,
  onPaymentComplete,
  onAddMoreServices,
  className = ''
}) => {
  // Checkout hook for mobile app context
  const checkout = useMobileAppCheckout({
    onSessionCreated: (session) => {
      setAmountPaid(session.total_amount || 0);
    },
    onPaymentSuccess: (result) => {
      // Mark groups as completed and close drawer
      onPaymentComplete(groups, result);
      onClose();
    },
    onPaymentError: (error) => {
      console.error('Mobile checkout payment error:', error);
    }
  });

  // State management
  const [activeTab, setActiveTab] = useState('items');
  const [selectedTipPercentage, setSelectedTipPercentage] = useState(0);
  const [customTipAmount, setCustomTipAmount] = useState(0);
  const [customDiscountAmount, setCustomDiscountAmount] = useState(0);
  const [customDiscountPercentage, setCustomDiscountPercentage] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState(0);
  
  // Additional products state (for retail items)
  const [additionalProducts, setAdditionalProducts] = useState([]);
  
  // Refs for focus management
  const drawerRef = useRef(null);
  const firstFocusableRef = useRef(null);
  
  // Fetch detailed checkout session when groups change (using checkout hook)
  useEffect(() => {
    let isCancelled = false;
    
    const fetchCheckoutSession = async () => {
      if (groups && groups.length > 0 && !isCancelled) {
        try {
          const groupIds = groups.map(g => g.id);
          await checkout.createSession(groupIds);
        } catch (error) {
          if (!isCancelled) {
            console.error('Failed to fetch mobile checkout session:', error);
          }
        }
      } else if (!isCancelled) {
        checkout.reset();
      }
    };
    
    fetchCheckoutSession();
    
    return () => {
      isCancelled = true;
    };
  }, [groups]);

  // Focus trap effect
  useEffect(() => {
    if (isOpen && !isMinimized && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0];
        firstFocusableRef.current.focus();
      }
      
      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };
      
      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen, isMinimized]);
  
  // Calculate totals
  const totals = useMemo(() => {
    // Use detailed services from checkout session instead of grouped data
    const servicesTotal = checkout.session?.services?.reduce((sum, service) => sum + parseFloat(service.price || 0), 0) || 0;
    const productsTotal = additionalProducts.reduce((sum, product) => sum + (product.price * product.quantity), 0);
    
    const subtotal = servicesTotal + productsTotal;
    const discountAmount = customDiscountPercentage > 0 
      ? (subtotal * customDiscountPercentage / 100)
      : customDiscountAmount;
    
    const afterDiscount = subtotal - discountAmount;
    const tipAmount = customTipAmount > 0 
      ? customTipAmount 
      : (afterDiscount * selectedTipPercentage / 100);
    
    const total = afterDiscount + tipAmount;
    
    return {
      subtotal,
      discountAmount,
      tipAmount,
      total,
      servicesTotal,
      productsTotal
    };
  }, [
    checkout.session?.services,
    additionalProducts,
    customDiscountPercentage,
    customDiscountAmount,
    selectedTipPercentage,
    customTipAmount
  ]);
  
  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };
  
  // Handle service removal
  const handleRemoveService = useCallback(async (serviceId) => {
    if (checkout.isLoading) return; // Prevent concurrent operations
    
    try {
      // Use the first group ID from checkout session (since services belong to appointment groups)
      const groupId = checkout.session?.group_ids?.[0] || groups[0]?.id;
      if (!groupId) {
        throw new Error('No group ID found for service removal');
      }
      
      await removeServiceFromAppointmentGroup(groupId, serviceId);
      
      // Refresh checkout session to reflect the removal
      const currentGroupIds = groups.map(g => g.id);
      await checkout.createSession(currentGroupIds);
      
      // Note: Removed auto-close when no services remain
      // User can manually close the modal or add more services
      
    } catch (error) {
      console.error('Erro ao remover serviço:', error);
      // TODO: Show user-friendly error message
    }
  }, [checkout.session?.group_ids, groups, checkout.createSession, checkout.isLoading]);
  
  // SwipeableServiceCard component
  const SwipeableServiceCard = React.memo(({ service, onRemove }) => {
    const [isRemoving, setIsRemoving] = useState(false);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const cardRef = useRef(null);
    const startX = useRef(0);
    const currentX = useRef(0);
    const timeoutRef = useRef(null);
    
    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    
    // Touch/Mouse handlers for swipe gesture
    const handleStart = (clientX, event) => {
      event.preventDefault();
      event.stopPropagation();
      startX.current = clientX;
      currentX.current = clientX;
    };

    const handleMove = (clientX, event) => {
      if (startX.current === 0) return;
      
      event.preventDefault();
      event.stopPropagation();
      currentX.current = clientX;
      const diff = clientX - startX.current;
      
      // Only allow left swipe (negative values)
      if (diff < 0) {
        setSwipeOffset(Math.max(diff, -120));
      }
    };

    const handleEnd = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const diff = currentX.current - startX.current;
      
      if (diff < -80) {
        // Trigger removal
        setIsRemoving(true);
        timeoutRef.current = setTimeout(() => {
          onRemove(service.id);
          timeoutRef.current = null;
        }, 200);
      } else {
        // Snap back
        setSwipeOffset(0);
      }
      
      startX.current = 0;
      currentX.current = 0;
    };
    
    return (
      <div className="relative overflow-hidden border border-brand-dark/10 dark:border-brand-light/10 bg-brand-light dark:bg-brand-dark">
        {/* Red background for swipe-to-delete */}
        <div className="absolute inset-0 bg-red-600 dark:bg-red-400 flex items-center justify-end pr-6">
          <div className="flex items-center space-x-2 text-white dark:text-black">
            <Trash2 size={16} strokeWidth={1.5} />
            <span className="font-medium text-sm uppercase tracking-wider">Remover</span>
          </div>
        </div>

        {/* Main card content */}
        <div
          ref={cardRef}
          className={`relative bg-brand-light dark:bg-brand-dark transition-transform duration-200 ${isRemoving ? 'opacity-0 -translate-x-full' : ''}`}
          style={{ transform: `translateX(${swipeOffset}px)` }}
          onTouchStart={(e) => e.touches.length > 0 && handleStart(e.touches[0].clientX, e)}
          onTouchMove={(e) => e.touches.length > 0 && handleMove(e.touches[0].clientX, e)}
          onTouchEnd={(e) => handleEnd(e)}
          onTouchCancel={(e) => handleEnd(e)}
          onMouseDown={(e) => handleStart(e.clientX, e)}
          onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX, e)}
          onMouseUp={(e) => handleEnd(e)}
          onMouseLeave={(e) => handleEnd(e)}
        >
          <div className="flex items-center justify-between p-4 border-b border-brand-dark/10 dark:border-brand-light/10">
            <div className="flex-1">
              <div className="font-medium text-sm text-brand-dark dark:text-brand-light mb-2 tracking-wide">
                {service.name}
              </div>
              <div className="text-xs text-brand-dark/50 dark:text-brand-light/50 mb-1 uppercase tracking-wider">
                {service.professional_name || 'Não atribuído'}
              </div>
              {service.duration_minutes && (
                <div className="flex items-center space-x-1">
                  <Clock size={12} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                  <span className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider">
                    {Math.floor(service.duration_minutes / 60)}h {service.duration_minutes % 60}min
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <span className="font-serif font-bold text-brand-dark dark:text-brand-light text-base">
                {formatPrice(service.price)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  });
  
  SwipeableServiceCard.displayName = 'SwipeableServiceCard';
  
  // Handle tip selection
  const handleTipSelect = (percentage) => {
    setSelectedTipPercentage(percentage);
    setCustomTipAmount(0); // Clear custom tip when percentage is selected
  };
  
  // Handle custom tip amount
  const handleCustomTipChange = (amount) => {
    setCustomTipAmount(amount);
    setSelectedTipPercentage(0); // Clear percentage when custom amount is set
  };
  
  // Handle adding more services to existing checkout
  const handleAddMoreServices = () => {
    if (onAddMoreServices && checkout.session) {
      onAddMoreServices({
        mode: 'add-to-existing',
        existingGroupId: checkout.session.group_ids?.[0], // First group ID
        existingClient: {
          id: checkout.session.client_id,
          name: checkout.session.client_name
        }
      });
    }
  };
  
  // Handle payment processing (using checkout hook)
  const handlePayment = async () => {
    try {
      // Extract client_id from checkout session or first group - required by backend validation
      const clientId = checkout.session?.client_id || groups[0]?.client_id;
      if (!clientId) {
        throw new Error('Client ID is required for payment processing');
      }

      const paymentData = {
        client_id: clientId, // Required by backend validation
        group_ids: groups.map(g => g.id),
        subtotal: totals.subtotal,
        discount_amount: totals.discountAmount,
        tip_amount: totals.tipAmount,
        total_amount: totals.total,
        payment_methods: [
          {
            method: paymentMethod,
            amount: totals.total
          }
        ],
        amount_paid: paymentMethod === 'cash' ? amountPaid : totals.total,
        change: paymentMethod === 'cash' ? Math.max(0, amountPaid - totals.total) : 0,
        additional_products: additionalProducts
      };
      
      // Process payment using the checkout hook
      // Success handling is done in the hook's onPaymentSuccess callback
      await checkout.processPayment(paymentData);
      
    } catch (error) {
      // Error handling is done in the hook's onPaymentError callback
      console.error('Mobile payment error:', error);
    }
  };
  
  // Handle drag over for adding groups
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const draggedGroupData = e.dataTransfer.getData('application/json');
      if (draggedGroupData) {
        const group = JSON.parse(draggedGroupData);
        // Validate that parsed data has required structure
        if (group && typeof group === 'object' && group.id && onGroupAdd) {
          onGroupAdd(group);
        } else {
          console.warn('Invalid group data structure in drag/drop');
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };
  
  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'items':
        return (
          <div className="space-y-4">

            {/* Service items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-3 border-b border-brand-dark/10 dark:border-brand-light/10">
                <h4 className="font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest text-xs">Serviços</h4>
                <button
                  onClick={() => handleAddMoreServices()}
                  className="text-brand-dark dark:text-brand-light text-xs uppercase tracking-wider hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 px-3 py-1 border border-brand-dark/20 dark:border-brand-light/20 transition-all duration-300"
                >
                  + Adicionar
                </button>
              </div>
              {checkout.session ? (
                <>
                  {/* Client info */}
                  <div className="flex items-center space-x-3 mb-4 p-3 border border-brand-dark/10 dark:border-brand-light/10">
                    <User size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                    <span className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{checkout.session?.client_name}</span>
                  </div>

                  {/* Individual services with swipe-to-delete */}
                  <div className="space-y-2">
                    {checkout.session?.services?.map((service, index) => (
                      <SwipeableServiceCard
                        key={service.id || `service-${index}`}
                        service={service}
                        index={index}
                        onRemove={handleRemoveService}
                      />
                    ))}
                  </div>

                  {/* Helper text for swipe gesture */}
                  {checkout.session?.services && checkout.session.services.length > 0 && (
                    <div className="text-center py-3 border-t border-brand-dark/10 dark:border-brand-light/10">
                      <p className="text-[10px] text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider">
                        ← Deslize para a esquerda para remover
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-3 animate-spin"></div>
                  <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Carregando...</p>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'products':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-brand-dark/10 dark:border-brand-light/10">
              <h4 className="font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest text-xs">Produtos</h4>
              <button className="text-brand-dark dark:text-brand-light text-xs uppercase tracking-wider hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 px-3 py-1 border border-brand-dark/20 dark:border-brand-light/20 transition-all duration-300">
                + Adicionar
              </button>
            </div>

            {additionalProducts.length === 0 ? (
              <div className="text-center py-12 border border-brand-dark/10 dark:border-brand-light/10">
                <Package size={40} className="mx-auto mb-4 text-brand-dark/20 dark:text-brand-light/20" strokeWidth={1.5} />
                <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Nenhum produto adicionado</p>
              </div>
            ) : (
              <div className="space-y-2">
                {additionalProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-brand-dark/10 dark:border-brand-light/10">
                    <div className="flex-1">
                      <span className="font-medium text-sm text-brand-dark dark:text-brand-light tracking-wide">{product.name}</span>
                      <div className="flex items-center space-x-2 mt-2">
                        <button className="p-1.5 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300">
                          <Minus size={12} strokeWidth={1.5} />
                        </button>
                        <span className="text-sm text-brand-dark dark:text-brand-light font-medium min-w-[30px] text-center">{product.quantity}</span>
                        <button className="p-1.5 border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300">
                          <Plus size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right flex items-center space-x-3">
                      <span className="font-serif font-bold text-brand-dark dark:text-brand-light">
                        {formatPrice(product.price * product.quantity)}
                      </span>
                      <button className="p-1.5 text-red-600 dark:text-red-400 border border-red-600/20 dark:border-red-400/20 hover:bg-red-600 hover:dark:bg-red-400 hover:text-white hover:dark:text-black transition-all duration-300">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        
      case 'discounts':
        return (
          <div className="space-y-4">
            <h4 className="font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest text-xs pb-3 border-b border-brand-dark/10 dark:border-brand-light/10">Descontos</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">Porcentagem (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={customDiscountPercentage}
                  onChange={(e) => {
                    const value = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                    setCustomDiscountPercentage(value);
                  }}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:outline-none focus:border-brand-dark dark:focus:border-brand-light transition-colors duration-300"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-[10px] text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">Valor Fixo (R$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customDiscountAmount}
                  onChange={(e) => {
                    const value = Math.max(0, parseFloat(e.target.value) || 0);
                    setCustomDiscountAmount(value);
                  }}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:outline-none focus:border-brand-dark dark:focus:border-brand-light transition-colors duration-300"
                  placeholder="0.00"
                />
              </div>

              {totals.discountAmount > 0 && (
                <div className="p-4 border border-green-600/20 dark:border-green-400/20 bg-transparent">
                  <p className="text-sm text-green-600 dark:text-green-400 tracking-wide">
                    Desconto Aplicado: <strong className="font-serif">{formatPrice(totals.discountAmount)}</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'payments':
        return (
          <div className="space-y-6">
            {/* Cash Payment Amount - Only shown when cash is selected */}
            {paymentMethod === 'cash' && (
              <div className="space-y-3">
                <h5 className="font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest text-xs pb-3 border-b border-brand-dark/10 dark:border-brand-light/10">Valor Recebido</h5>
                <input
                  type="number"
                  step="0.01"
                  value={amountPaid}
                  onChange={(e) => {
                    const value = Math.max(0, parseFloat(e.target.value) || 0);
                    setAmountPaid(value);
                  }}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:outline-none focus:border-brand-dark dark:focus:border-brand-light transition-colors duration-300"
                  placeholder="0.00"
                />

                {amountPaid > totals.total && (
                  <div className="p-4 border border-amber-600/20 dark:border-amber-400/20 bg-transparent">
                    <p className="text-amber-600 dark:text-amber-400 text-sm tracking-wide">
                      Troco: <strong className="font-serif">{formatPrice(amountPaid - totals.total)}</strong>
                    </p>
                  </div>
                )}

                {amountPaid < totals.total && amountPaid > 0 && (
                  <div className="p-4 border border-red-600/20 dark:border-red-400/20 bg-transparent">
                    <p className="text-red-600 dark:text-red-400 text-sm tracking-wide">
                      Faltam: <strong className="font-serif">{formatPrice(totals.total - amountPaid)}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tip selection */}
            <div>
              <h5 className="font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest text-xs pb-3 border-b border-brand-dark/10 dark:border-brand-light/10 mb-4">Gorjeta</h5>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[0, 10, 15, 18].map((percentage) => (
                  <button
                    key={percentage}
                    onClick={() => handleTipSelect(percentage)}
                    className={`py-3 px-2 border text-xs font-medium uppercase tracking-wider transition-all duration-300 ${
                      selectedTipPercentage === percentage
                        ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border-brand-dark dark:border-brand-light'
                        : 'bg-transparent text-brand-dark dark:text-brand-light border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                    }`}
                  >
                    {percentage === 0 ? 'Sem' : `${percentage}%`}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-[10px] text-brand-dark/50 dark:text-brand-light/50 mb-2 uppercase tracking-wider">Valor Personalizado (R$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customTipAmount}
                  onChange={(e) => {
                    const value = Math.max(0, parseFloat(e.target.value) || 0);
                    handleCustomTipChange(value);
                  }}
                  className="w-full px-4 py-3 border border-brand-dark/20 dark:border-brand-light/20 bg-brand-light dark:bg-brand-dark text-brand-dark dark:text-brand-light focus:outline-none focus:border-brand-dark dark:focus:border-brand-light transition-colors duration-300"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div
      className={`fixed inset-0 bg-brand-light dark:bg-brand-dark z-50 transform transition-transform ${
        isMinimized ? 'translate-y-full' : 'translate-y-0'
      } ${className}`}
      ref={drawerRef}
    >
      <div className="h-full flex flex-col">
        {/* Header - Luxury Design */}
        <div className="flex items-center justify-between p-6 border-b border-brand-dark/10 dark:border-brand-light/10 bg-brand-dark dark:bg-brand-light">
          <h3 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Finalizar Pagamento</h3>
          <div className="flex space-x-2">
            <button
              onClick={onMinimize}
              className="p-2 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300 touch-manipulation"
              title="Minimizar"
              aria-label="Minimizar checkout"
            >
              <Minimize2 size={20} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
            </button>
            <button
              onClick={onClose}
              className="p-2 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300 touch-manipulation"
              title="Fechar"
              aria-label="Fechar checkout"
            >
              <X size={20} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        
        {/* Tabs - Luxury Design */}
        <div className="flex border-b border-brand-dark/10 dark:border-brand-light/10" role="tablist" aria-label="Checkout sections">
          {[
            { id: 'items', label: 'Itens', icon: ShoppingCart },
            { id: 'products', label: 'Produtos', icon: Package },
            { id: 'discounts', label: 'Desconto', icon: Percent },
            { id: 'payments', label: 'Pagamento', icon: CreditCard }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`mobile-tabpanel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center justify-center space-y-1.5 py-4 text-[10px] font-medium uppercase tracking-wider transition-all duration-300 touch-manipulation ${
                  activeTab === tab.id
                    ? 'text-brand-dark dark:text-brand-light bg-brand-dark/5 dark:bg-brand-light/5 border-b-2 border-brand-dark dark:border-brand-light'
                    : 'text-brand-dark/50 dark:text-brand-light/50 hover:text-brand-dark dark:hover:text-brand-light hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                }`}
              >
                <Icon size={16} strokeWidth={activeTab === tab.id ? 2 : 1.5} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
        
        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-4 pb-safe" role="tabpanel" id={`mobile-tabpanel-${activeTab}`} aria-labelledby={`mobile-tab-${activeTab}`}>
          {renderTabContent()}
        </div>
        
        {/* Payment error - Luxury Design */}
        {checkout.error && (
          <div className="p-4 border-t border-red-600/20 dark:border-red-400/20 bg-transparent" role="alert" aria-live="polite">
            <p className="text-red-600 dark:text-red-400 text-sm tracking-wide">{checkout.error}</p>
          </div>
        )}

        {/* Footer with totals and pay button - Luxury Design */}
        <div className="border-t border-brand-dark/10 dark:border-brand-light/10 p-6 pb-safe space-y-5 bg-brand-light dark:bg-brand-dark">
          {/* Totals breakdown */}
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center pb-2">
              <span className="text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider text-xs">Subtotal:</span>
              <span className="font-serif font-bold text-brand-dark dark:text-brand-light">{formatPrice(totals.subtotal)}</span>
            </div>
            {totals.discountAmount > 0 && (
              <div className="flex justify-between items-center text-red-600 dark:text-red-400 pb-2">
                <span className="uppercase tracking-wider text-xs">Desconto:</span>
                <span className="font-serif font-bold">-{formatPrice(totals.discountAmount)}</span>
              </div>
            )}
            {totals.tipAmount > 0 && (
              <div className="flex justify-between items-center pb-2">
                <span className="text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider text-xs">
                  Gorjeta {selectedTipPercentage > 0 ? `(${selectedTipPercentage}%)` : ''}:
                </span>
                <span className="font-serif font-bold text-brand-dark dark:text-brand-light">{formatPrice(totals.tipAmount)}</span>
              </div>
            )}
            <div className="flex justify-between items-center font-bold text-lg border-t border-brand-dark/10 dark:border-brand-light/10 pt-4">
              <span className="font-serif text-brand-dark dark:text-brand-light tracking-wide">Total:</span>
              <span className="font-serif text-brand-dark dark:text-brand-light text-2xl">{formatPrice(totals.total)}</span>
            </div>
          </div>
          
          {/* Payment method quick selector - Luxury Design */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'cash', label: 'Dinheiro', icon: DollarSign },
              { id: 'debit', label: 'Débito', icon: CreditCard },
              { id: 'credit', label: 'Crédito', icon: CreditCard },
              { id: 'pix', label: 'PIX', icon: DollarSign },
            ].map((method) => {
              const Icon = method.icon;
              return (
                <button
                  key={method.id}
                  onClick={() => setPaymentMethod(method.id)}
                  className={`p-3 border transition-all text-[10px] font-medium uppercase tracking-wider touch-manipulation ${
                    paymentMethod === method.id
                      ? 'border-brand-dark dark:border-brand-light bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark'
                      : 'border-brand-dark/20 dark:border-brand-light/20 bg-transparent text-brand-dark dark:text-brand-light hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                  }`}
                >
                  <Icon size={14} className="mx-auto mb-1.5" strokeWidth={1.5} />
                  <div className="truncate">{method.label}</div>
                </button>
              );
            })}
          </div>

          {/* Enhanced pay button - Luxury Design */}
          <button
            onClick={handlePayment}
            disabled={checkout.isLoading || groups.length === 0 || (paymentMethod === 'cash' && amountPaid < totals.total)}
            className="w-full bg-brand-dark dark:bg-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 disabled:bg-brand-dark/30 dark:disabled:bg-brand-light/30 disabled:cursor-not-allowed text-brand-light dark:text-brand-dark font-medium py-5 px-6 border border-brand-dark dark:border-brand-light transition-all duration-300 flex items-center justify-between touch-manipulation uppercase tracking-widest text-sm"
          >
            <div className="flex items-center space-x-3">
              {checkout.isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-brand-light dark:border-brand-dark border-t-transparent animate-spin" aria-hidden="true"></div>
                  <span aria-live="polite">Processando...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={20} strokeWidth={1.5} />
                  <span>Pagar {formatPrice(totals.total)}</span>
                </>
              )}
            </div>

            {/* Selected payment method indicator */}
            {!checkout.isLoading && (
              <div className="flex items-center space-x-1.5 bg-brand-light/20 dark:bg-brand-dark/20 px-2.5 py-1 border border-brand-light/30 dark:border-brand-dark/30">
                {paymentMethod === 'cash' && <DollarSign size={14} strokeWidth={1.5} />}
                {(paymentMethod === 'debit' || paymentMethod === 'credit') && <CreditCard size={14} strokeWidth={1.5} />}
                {paymentMethod === 'pix' && <DollarSign size={14} strokeWidth={1.5} />}
                <span className="text-[10px] uppercase tracking-wider">
                  {paymentMethod === 'cash' && 'Dinheiro'}
                  {paymentMethod === 'debit' && 'Débito'}
                  {paymentMethod === 'credit' && 'Crédito'}
                  {paymentMethod === 'pix' && 'PIX'}
                </span>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type safety
CheckoutDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isMinimized: PropTypes.bool,
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    total_price: PropTypes.number,
    client_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    client_name: PropTypes.string
  })),
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func,
  onGroupAdd: PropTypes.func,
  onPaymentComplete: PropTypes.func,
  onAddMoreServices: PropTypes.func,
  className: PropTypes.string
};

CheckoutDrawer.defaultProps = {
  isMinimized: false,
  groups: [],
  onMinimize: null,
  onGroupAdd: null,
  onPaymentComplete: null,
  onAddMoreServices: null,
  className: ''
};

export default CheckoutDrawer;
